/**
 * Suspend the Web Audio context whenever no sound has played for a while.
 *
 * Howler keeps its AudioContext "running" between sounds, which keeps an
 * AAudio mixer thread alive in the native process - measured at ~10% of a
 * core on a Galaxy S24+, mixing silence for the whole session. Howler's own
 * autoSuspend is disabled outright by EN-10512's audio work (see
 * utils/audioResume - a suspended context makes the next cue arrive late),
 * so this watchdog is the only thing reclaiming that idle cost.
 *
 * RECONCILIATION with EN-10512 (see investigation doc 8ab): the two goals -
 * no idle mixer heat, prompt reliable cues - share one context, so this
 * watchdog gives ground on two points:
 *
 *  1. THE STATE FLAG MUST STAY HONEST. `Howl.play()` gates on Howler's OWN
 *     `state` flag, not the context's: suspending the context while the flag
 *     still reads 'running' makes the next engine-driven cue start a source
 *     on a suspended context - silently dropped, and nothing ever resumes it
 *     (audioResume.ts documents the verified howler.js internals). Setting
 *     the flag alongside the suspend makes that cue PARK instead, and
 *     Howler's _autoResume revives it a few ms later. A slightly late cue
 *     over a missing one.
 *  2. THE WINDOW IS 15s, NOT 3s. At 3s the suspend fired between betting
 *     rounds, so cues routinely paid the resume round-trip - the exact
 *     "showdown flip sounds late" symptom EN-10512 fixed. At 15s it fires in
 *     the genuinely quiet contexts the heat measurement came from (the
 *     lobby, an untouched table), and rarely mid-hand.
 *
 * MUTED PLAYERS SKIP THE WAIT: with `Howler.mute(true)` (GlobalContext's
 * hijack_muted) every cue is silent anyway, so the mixer is pure cost -
 * suspend as soon as the context runs. EN-10512's gesture handlers may
 * resume it on a tap; the next tick re-suspends.
 *
 * Howler is lazy-loaded by use-sound, so we read it off window and no-op
 * until it exists. See docs/investigations/20260819-game-client-overheating.md
 * (sections 8e and 8ab).
 */
export function installAudioIdleSuspend(idleMs = 15_000, checkMs = 1000): () => void {
  let idleSince: number | null = null;
  const id = window.setInterval(() => {
    const howler = (window as any).Howler;
    const ctx: AudioContext | undefined = howler?.ctx;
    if (!ctx || ctx.state !== 'running' || howler.state === 'suspending') {
      idleSince = null;
      return;
    }
    const suspend = () => {
      idleSince = null;
      // Honest bookkeeping BEFORE the (async) suspend - see docblock point 1.
      howler.state = 'suspended';
      ctx.suspend().catch(() => {
        /* context already closing/suspended */
      });
    };
    if (howler._muted === true) {
      suspend();
      return;
    }
    const anyPlaying = (howler._howls || []).some((h: any) => {
      try {
        return h.playing();
      } catch {
        return false;
      }
    });
    if (anyPlaying) {
      idleSince = null;
      return;
    }
    if (idleSince === null) {
      idleSince = Date.now();
      return;
    }
    if (Date.now() - idleSince >= idleMs) {
      suspend();
    }
  }, checkMs);
  return () => window.clearInterval(id);
}

export default installAudioIdleSuspend;
